import React, { FC, useEffect, useReducer, useState } from "react"
import localforage from "localforage"

// Utils
import { isClientSide } from "@utils"
import { logCustomGAEvent } from "./_utils"

// Shared components
import Button, { BUTTON_VARIANTS } from "@components/Button/Button"
import Modal from "@components/Modal/Modal"
import SEO from "@components/SEO/SEO"

// App
import InterviewBuilderApp from "./_components/InterviewBuilderApp/InterviewBuilderApp"

// Store/reducer
import { loadState } from "./_store/actions"
import { GA_CUSTOM_EVENTS, LOCAL_FORAGE_KEY, STATES, STATE_PRESETS } from "./_store/constants"
import { InterviewBuilderReducer } from "./_store/reducer"
import { StateType } from "./_store/types"

// Page components
import InterviewBuilderHero from "./_components/InterviewBuilderHero/InterviewBuilderHero"
import InterviewBuilderStartPanels from "./_components/InterviewBuilderStartPanels/InterviewBuilderStartPanels"

// Styled Components
import { InterviewBuilderPageContentDIV, InterviewBuilderPageModalDIV } from "./index.styles"

export const Head = () => (
  <SEO
    description="View sample job descriptions, select from 60+ targeted questions, and quickly and easily build the most effective interview for your next customer-facing role."
    image="/images/pages/interview-builder/InterviewBuilder--meta.png"
    permalink="/interview-builder/"
    title="Free Customer Service Interview Builder by Help Scout"
  />
)

const InterviewBuilderPage: FC = () => {
  const [statePreset, setStatePreset] = useState<STATE_PRESETS | null>(null) // setting a default for easier development
  const [state, dispatch] = useReducer(InterviewBuilderReducer, STATES.DEFAULT)
  const [confirmOverwriteModalPresetName, setConfirmOverwriteModalPresetName] = useState<STATE_PRESETS | null>(null)

  const hasStartedInterview = state.interview.questions.length > 0

  // Check localforage (IndexedDB) for a saved state on the user's machine and load it if so
  // (but only if the DEFAULT preset has been chosen in the selection screen)
  useEffect(() => {
    if (!isClientSide()) return

    // Check the query string for a preset type
    const match = window.location.search.match(/preset=(([A-Z]|_)+)&?/)

    // If the query string includes a valid presetName, load it!
    if (match && Object.keys(STATE_PRESETS).includes(match[1])) {
      setStatePreset(STATE_PRESETS[match[1]])
      dispatch(loadState(STATES[match[1]]))
      return
    }

    // If there isn't a preset included in the query string, check if a saved state is in localforage
    const loadStateFromLocalforage = async () => {
      const existingStateStr: string | null | undefined =
        await localforage.getItem(LOCAL_FORAGE_KEY)

      if (typeof existingStateStr === "string") {
        const existingState: StateType = JSON.parse(existingStateStr)
        dispatch(loadState(existingState))
      }
    }
    loadStateFromLocalforage()
  }, [])

  const chooseStatePreset = (presetName: STATE_PRESETS) => {
    setStatePreset(presetName)

    // If the interview has already been started and the chosen preset is DEFAULT then we have already loaded the cached state in the
    // useEffect hook above
    if (!(hasStartedInterview && presetName === STATE_PRESETS.DEFAULT)) dispatch(loadState(STATES[presetName]))

    logCustomGAEvent(GA_CUSTOM_EVENTS.SELECT_PRESET_INTERVIEW, {
      preset: presetName,
    })
  }

  const handleChooseStatePreset = (presetName: STATE_PRESETS) => {
    // If an Interview has been started and DEFAULT is selected, then we are continuing an existing Interview
    // If an Interview has been started and something other than DEFAULT is selected then we are overwriting an existing Interivew
    // Otherwise, we're starting fresh from a preset
    if (hasStartedInterview && presetName !== STATE_PRESETS.DEFAULT) {
      setConfirmOverwriteModalPresetName(presetName)
      return
    }
    chooseStatePreset(presetName)
  }

  return (
    <>
      <InterviewBuilderHero
        activeTab="/interview-builder/"
        backLink={statePreset ? () => setStatePreset(null) : undefined}
      />
      <InterviewBuilderPageContentDIV className="background-color--cobalt-100">
        <div className="container">    
          {!statePreset ? (
            <InterviewBuilderStartPanels
              hasStartedInterview={hasStartedInterview}
              onSelectStatePreset={handleChooseStatePreset}
            />
          ) : (
            <InterviewBuilderApp
              dispatch={dispatch}
              state={state}
              statePreset={statePreset}
            />
          )}        
        </div>
      </InterviewBuilderPageContentDIV>
      {confirmOverwriteModalPresetName && (
        <Modal
          onClose={() => setConfirmOverwriteModalPresetName(null)}
        >
          {(closeModal) => (
            <InterviewBuilderPageModalDIV>
              <h4>Are you sure?</h4>
              <p>If you choose to get started with a sample interview any previous progress you made will be lost.</p>
              <div>
                <Button
                  onClick={closeModal}
                  variant={BUTTON_VARIANTS.SECONDARY}
                >
                  Oops, nevermind
                </Button>
                <Button
                  onClick={() => {
                    chooseStatePreset(confirmOverwriteModalPresetName)
                    setConfirmOverwriteModalPresetName(null)
                    closeModal()
                  }}
                  variant={BUTTON_VARIANTS.WARNING}
                >
                  Continue
                </Button>
              </div>
            </InterviewBuilderPageModalDIV>
          )}
        </Modal>
      )}
    </>
  )
}

export default InterviewBuilderPage
